import { FormInput, InfoBox, TabsNavigation } from 'components/molecules';
import { TopBar } from 'components/organisms';
import { TopbarTabs } from 'components/templates';
import { Form, Formik, FormikConfig } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ILabelReportDetailResponse } from 'services/label-report/types';
import { II18nextPrefix } from 'types/components/components.interface';
import { getSettlementPeriodName } from 'utils/global/name.utils';
import { IFormProps } from '../types';
import LabelReportContractEarnings from './tabs/LabelReportContractEarnings';
import LabelReportGroupedChannels from './tabs/LabelReportGroupedChannels';
import LabelReportGroupedProducts from './tabs/LabelReportGroupedProducts';
import LabelReportGroupedSalesChannelList from './tabs/LabelReportGroupedSalesChannelList';

export enum LabelReportTabs {
  contractEarnings,
  groupedProducts,
  groupedChannels,
  groupedSalesChannelList,
}

export type ILabelReportFormValues = ILabelReportDetailResponse & {
  periodFromDisplay?: string;
  periodToDisplay?: string;
  amountEarnedDisplay?: string;
  amountForLabelDisplay?: string;
  amountForOwnerDisplay?: string;
};

export const initialLabelReportDetail: ILabelReportFormValues = {
  id: '',
  labelReportNumber: '',
  startSettlementPeriodId: '',
  endSettlementPeriodId: '',
  labelId: '',
  labelName: '',
  amountEarned: 0,
  amountForLabel: 0,
  amountForOwner: 0,
  sendStatus: 0,
  isOutdated: false,
  canGenerateDetailedReport: false,
  filePath: '',
  detailedFilePath: '',
  contractEarnings: [],
  startSettlementPeriodName: { name: '', year: 0 },
  endSettlementPeriodName: { name: '', year: 0 },
  groupedProductData: [],
  groupedChannelData: [],
  groupedSalesChannelList: [],
  groupedChannels: [],
  groupedProducts: [],
  periodFromDisplay: '',
  periodToDisplay: '',
  amountEarnedDisplay: '',
  amountForLabelDisplay: '',
  amountForOwnerDisplay: '',
};

interface LabelReportFormProps
  extends FormikConfig<ILabelReportFormValues>,
    IFormProps,
    II18nextPrefix {}

interface LabelReportFormComponent extends React.FC<LabelReportFormProps> {
  getRouterTabs?: (
    disabledChildren?: string[]
  ) => { path: string; element: JSX.Element }[];
}

const LabelReportForm: LabelReportFormComponent = ({
  i18nextPrefix = 'label-reports',
  initialValues = initialLabelReportDetail as ILabelReportFormValues,
  tittle = '',
  onSubmit = () => {},
  topbarContent,
  status,
  backPath,
}) => {
  const { t } = useTranslation();

  const periodTitle =
    initialValues.labelReportNumber ||
    (initialValues.startSettlementPeriodName?.name &&
    initialValues.endSettlementPeriodName?.name
      ? `${getSettlementPeriodName(initialValues.startSettlementPeriodName)} - ${getSettlementPeriodName(initialValues.endSettlementPeriodName)}`
      : t('label-reports.report'));

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      initialStatus={status}
      onSubmit={onSubmit}
      validateOnChange={true}
      validateOnBlur={true}>
      {({ values }) => (
        <Form className='w-full h-full'>
          <TopbarTabs>
            <TopBar
              title={periodTitle}
              topbarContent={topbarContent}
              path={backPath}
            >
              <InfoBox>
                <InfoBox.Items columns={3}>
                  <FormInput.Text
                    name='labelName'
                    label={t('input.label.labelName')}
                  />
                  <FormInput.Text
                    name='periodFromDisplay'
                    label={t('input.label.periodFrom')}
                    disabled
                  />
                  <FormInput.Text
                    name='periodToDisplay'
                    label={t('input.label.periodTo')}
                    disabled
                  />
                  <FormInput.Text
                    name='amountEarnedDisplay'
                    label={t('input.label.amountEarned')}
                    disabled
                  />
                  <FormInput.Text
                    name='amountForLabelDisplay'
                    label={t('input.label.amountForLabel')}
                    disabled
                  />
                  <FormInput.Text
                    name='amountForOwnerDisplay'
                    label={t('input.label.amountForOwner')}
                    disabled
                  />
                </InfoBox.Items>
              </InfoBox>
              <TabsNavigation
                tabs={[
                  {
                    value: LabelReportTabs.contractEarnings,
                    name: 'contractEarnings',
                  },
                  {
                    value: LabelReportTabs.groupedProducts,
                    name: 'groupedProducts',
                  },
                  {
                    value: LabelReportTabs.groupedChannels,
                    name: 'groupedChannels',
                  },
                  {
                    value: LabelReportTabs.groupedSalesChannelList,
                    name: 'groupedSalesChannelList',
                  },
                ]}
                i18nextPrefix={i18nextPrefix}
              />
            </TopBar>
          </TopbarTabs>
        </Form>
      )}
    </Formik>
  );
};

LabelReportForm.getRouterTabs = (disabledChildren: string[] = []) =>
  [
    {
      path: 'contractEarnings',
      element: <LabelReportContractEarnings />,
    },
    { path: 'groupedProducts', element: <LabelReportGroupedProducts /> },
    { path: 'groupedChannels', element: <LabelReportGroupedChannels /> },
    {
      path: 'groupedSalesChannelList',
      element: <LabelReportGroupedSalesChannelList />,
    },
  ].filter(child => !disabledChildren.includes(child.path));

export default LabelReportForm;
